import { Button } from '@mantine/core';
import { IconDeviceMobileDown, IconX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useInstallPwa } from 'src/Hooks/Shared/useInstallPwa';

import styles from './InstallBanner.module.scss';

const DISMISSED_KEY = 'install-banner-dismissed-at';
const SESSIONS_KEY = 'install-banner-session-count';
const SESSION_FLAG = 'install-banner-session-counted';
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
const MIN_SESSIONS_BEFORE_SHOW = 2;
const HIDDEN_ROUTES = ['/install', '/auth', '/onboarding', '/accept-terms', '/join'];

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as unknown as { standalone?: boolean }).standalone === true;

const isIosSafari = () => {
  const ua = navigator.userAgent;
  if (!/iPhone|iPad|iPod/i.test(ua)) return false;
  return !/CriOS|FxiOS|EdgiOS|OPiOS|Instagram|FBAN|FBAV|Line/i.test(ua);
};

const isAndroid = () => /Android/i.test(navigator.userAgent);

const incrementSessionCount = () => {
  if (sessionStorage.getItem(SESSION_FLAG)) return;
  sessionStorage.setItem(SESSION_FLAG, '1');
  const current = Number(localStorage.getItem(SESSIONS_KEY) ?? '0');
  localStorage.setItem(SESSIONS_KEY, String(current + 1));
};

const wasRecentlyDismissed = () => {
  const dismissedAt = localStorage.getItem(DISMISSED_KEY);
  if (!dismissedAt) return false;
  return Date.now() - Number(dismissedAt) < DISMISS_DURATION_MS;
};

const InstallBanner = () => {
  const { supportsPWA, installPwa } = useInstallPwa();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    incrementSessionCount();
  }, []);

  if (dismissed) return null;
  if (typeof window === 'undefined') return null;
  if (isStandalone()) return null;
  if (HIDDEN_ROUTES.some(route => location.pathname.startsWith(route))) return null;
  if (wasRecentlyDismissed()) return null;

  const sessions = Number(localStorage.getItem(SESSIONS_KEY) ?? '0');
  if (sessions < MIN_SESSIONS_BEFORE_SHOW) return null;

  const onAndroid = isAndroid();
  const onIosSafari = isIosSafari();

  if (!onAndroid && !onIosSafari) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setDismissed(true);
  };

  const handleInstall = () => {
    if (onAndroid && supportsPWA) {
      installPwa();
    } else {
      navigate('/install');
    }
  };

  return (
    <div className={styles.container} role="status">
      <div className={styles.icon}>
        <IconDeviceMobileDown size={18} />
      </div>
      <div className={styles.text}>
        Installez Share Heritage sur votre téléphone, accès rapide en 1 minute.
      </div>
      <Button
        size="xs"
        variant="white"
        color="dark"
        className={styles.button}
        onClick={handleInstall}
      >
        Installer
      </Button>
      <button
        type="button"
        className={styles.closeButton}
        aria-label="Fermer"
        onClick={handleDismiss}
      >
        <IconX size={18} />
      </button>
    </div>
  );
};

export default InstallBanner;
